import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { Avatar } from "primereact/avatar";
import { Button } from "primereact/button";
// TODO: reativar com "Mostrar e Ocultar Colunas".
// import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import type { DataTableStateEvent } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import { Paginator } from "primereact/paginator";
import type { PaginatorPageChangeEvent, PaginatorTemplateOptions } from "primereact/paginator";
import { Toast } from "primereact/toast";

import { USERS_TABLE_DENSITY_KEY } from "@/core/constants/keys";
import { getErrorMessage, isAPIError } from "@/core/services";
import { useTableExport } from "@/modules/export";
import { useRoleOptions } from "@/modules/roles";
import {
  DEFAULT_USERS_SORT,
  USER_STATUS_LABEL,
  buildUsersSort,
  getUserInitials,
  listAllUsers,
  parseUsersSort,
  updateUserRole,
  updateUserStatus,
  useUsers,
} from "@/modules/users";
import type {
  User,
  UserFilterOption,
  UsersSort,
  UsersSortField,
  UserStatus,
} from "@/modules/users";
import {
  ACTION_TOOLTIP_OPTIONS,
  EDIT_ACTION_TOOLTIP,
  toggleActionTooltip,
} from "../components/action-tooltip";
import { EmptyState, resolveEmptyStateKind } from "../components/empty-state";
import type { EmptyStateContent } from "../components/empty-state";
import { createExportFeedback, ExportTableDialog } from "../components/export-table-dialog";
import { StateMessage } from "../components/state-message";
import { EditUserDialog } from "./components/edit-user-dialog";
import type { EditUserValues } from "./components/edit-user-dialog";
import { ToggleUserStatusDialog } from "./components/toggle-user-status-dialog";
import styles from "./users-screen.module.scss";

/** Tamanho de página inicial conforme o protótipo ("Exibindo 10 de 142 usuários cadastrados"). */
const DEFAULT_PAGE_SIZE = 10;

/** Quantidades de usuários por página oferecidas ao administrador. */
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const PAGE_SIZE_DROPDOWN_OPTIONS = PAGE_SIZE_OPTIONS.map((rows) => ({
  label: `${rows} por página`,
  value: rows,
}));

const EMPTY_CELL = "—";

/** Sinalização da RN004 na célula de Perfil e na exportação da visualização. */
const NO_ROLE_LABEL = "Sem perfil";

/** Colunas visíveis da listagem, na ordem da tabela (AC2 da US1442). */
const EXPORT_COLUMNS = ["Nome", "E-mail", "Time", "Perfil", "Última Atividade", "Status"];

const EXPORT_FILE_NAME = "usuarios";
const EXPORT_TITLE = "Usuários";

/** Base sem usuários: a listagem está vazia porque ainda não há o que exibir. */
const EMPTY_STATE: EmptyStateContent = {
  title: "Nenhum usuário cadastrado",
  description: "Assim que houver usuários no Hub, eles aparecerão nesta listagem.",
};

/** Busca no input sem registros — `ilu-no-search` do Figma. */
const EMPTY_SEARCH_STATE: EmptyStateContent = {
  title: "Nenhum usuário encontrado para essa busca.",
  description: "Verifique o termo digitado ou tente buscar com outras palavras.",
};

/** Filtros aplicados sem registros — `ilu-no-filters` do Figma. */
const EMPTY_FILTER_STATE: EmptyStateContent = {
  title: "Nenhum usuário corresponde aos filtros selecionados.",
  description: "Tente ajustar ou limpar os filtros",
};

/** Sem seleção o filtro não restringe a consulta; o rótulo é o próprio nome do campo. */
const ANY_TIME: UserFilterOption = { label: "Time", value: "" };
const ANY_ROLE: UserFilterOption = { label: "Perfil", value: "" };
const TIME_FILTER_PLACEHOLDER = "Buscar time";
const ROLE_FILTER_PLACEHOLDER = "Buscar perfil";
const TIME_EMPTY_FILTER_MESSAGE = "Nenhum time encontrado.";
const ROLE_EMPTY_FILTER_MESSAGE = "Nenhum perfil encontrado.";

/** Retorno da atribuição/alteração de perfil (RN01/RN02). */
export const ROLE_UPDATE_SUCCESS_MESSAGE = "Perfil do usuário atualizado com sucesso.";
export const ROLE_UPDATE_ERROR_MESSAGE = "Não foi possível atualizar o perfil do usuário.";
export const ROLE_UPDATE_FORBIDDEN_MESSAGE =
  "Você não tem permissão para administrar os usuários do Hub.";
export const SESSION_EXPIRED_MESSAGE = "Sua sessão expirou. Entre novamente para continuar.";

/** O administrador precisa distinguir "não tenho permissão" de "deu erro". */
const UPDATE_ERROR_BY_STATUS: Record<number, { summary: string; detail: string }> = {
  401: { summary: "Sessão expirada", detail: SESSION_EXPIRED_MESSAGE },
  403: { summary: "Acesso não autorizado", detail: ROLE_UPDATE_FORBIDDEN_MESSAGE },
};

type Density = "comfortable" | "compact";

/**
 * "Tamanho da tabela" dos Padrões Globais de Interface: confortável é o padrão
 * e compacto reduz o espaçamento para exibir mais registros por tela.
 */
const DENSITY_OPTIONS: { value: Density; label: string; description: string }[] = [
  {
    value: "comfortable",
    label: "Confortável",
    description:
      "Exibe as linhas com mais espaçamento, facilitando a leitura e diminuindo a fadiga visual.",
  },
  {
    value: "compact",
    label: "Compacto",
    description:
      "Reduz o espaçamento entre linhas para exibir mais registros simultaneamente por tela.",
  },
];

/**
 * "Mostrar e Ocultar Colunas" do dialog de Configuração da Tabela, com os campos
 * definidos no protótipo (node 4950-61475). Esses rótulos são os do modelo
 * compartilhado do Hub e ainda não correspondem às colunas desta listagem, por
 * isso a seleção é apenas guardada — a ligação com as colunas do grid depende de
 * confirmação do desenho para a tela de Usuários.
 */
const COLUMN_OPTIONS: { value: string; label: string }[] = [
  { value: "dataHora", label: "Data/Hora" },
  { value: "valorAnteriorNovo", label: "Valor Anterior -> Novo Valor" },
  { value: "justificativa", label: "Justificativa" },
  { value: "modulo", label: "Módulo" },
  { value: "responsavel", label: "Responsável" },
];

const ALL_COLUMNS_VISIBLE = COLUMN_OPTIONS.map((option) => option.value);

function isDensity(value: string | null): value is Density {
  return value === "comfortable" || value === "compact";
}

function readStoredDensity(): Density {
  try {
    const stored = localStorage.getItem(USERS_TABLE_DENSITY_KEY);
    return isDensity(stored) ? stored : "comfortable";
  } catch {
    return "comfortable";
  }
}

function persistDensity(value: Density) {
  try {
    localStorage.setItem(USERS_TABLE_DENSITY_KEY, value);
  } catch {
    return;
  }
}

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "short",
  timeStyle: "short",
});

function formatLastActivity(value: string): string {
  if (!value) return EMPTY_CELL;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : DATE_TIME_FORMATTER.format(date);
}

function toExportRow(user: User): string[] {
  return [
    user.nome,
    user.email,
    user.time,
    user.papel || NO_ROLE_LABEL,
    user.ultimaAtividade ? formatLastActivity(user.ultimaAtividade) : "",
    USER_STATUS_LABEL[user.status],
  ];
}

/** Confirmação apresentada ao administrador quando a alteração é gravada. */
export const ACTIVATE_SUCCESS_MESSAGE = "Usuário ativado com sucesso.";
export const DEACTIVATE_SUCCESS_MESSAGE = "Usuário inativado com sucesso.";

export const TOGGLE_STATUS_ERROR_MESSAGE = "Não foi possível alterar o status do usuário.";
export const TOGGLE_STATUS_FORBIDDEN_MESSAGE =
  "Você não tem permissão para alterar o status dos usuários do Hub.";
export const TOGGLE_STATUS_SESSION_EXPIRED_MESSAGE =
  "Sua sessão expirou. Entre novamente no Hub para alterar o status do usuário.";
export const TOGGLE_STATUS_CONFLICT_MESSAGE =
  "O status deste usuário já foi alterado por outra pessoa. Atualize a listagem antes de tentar novamente.";

/**
 * Situações em que repetir a operação não muda o resultado: o modal se fecha e a
 * listagem segue com o status anterior. A falha de gravação não entra na lista,
 * por isso mantém o modal aberto para nova tentativa.
 */
const TOGGLE_STATUS_ERRORS: Record<number, { summary: string; detail: string }> = {
  // A sessão expirada é tratada pelo cliente HTTP, que redireciona ao login;
  // este ramo cobre um 401 que chegue à tela sem o redirecionamento.
  401: { summary: "Sessão expirada", detail: TOGGLE_STATUS_SESSION_EXPIRED_MESSAGE },
  403: { summary: "Acesso não autorizado", detail: TOGGLE_STATUS_FORBIDDEN_MESSAGE },
  409: { summary: "Status já alterado", detail: TOGGLE_STATUS_CONFLICT_MESSAGE },
};

function buildSummary(shown: number, total: number, filtered: boolean): string {
  const noun = total === 1 ? "usuário" : "usuários";
  const qualifier = filtered ? "encontrado" : "cadastrado";

  return `Exibindo ${shown} de ${total} ${noun} ${qualifier}${total === 1 ? "" : "s"}`;
}

function papelIdFromSearch(params: URLSearchParams): string {
  return params.get("papelId")?.trim() ?? "";
}

export function UsersScreen() {
  const configPanel = useRef<OverlayPanel>(null);
  const toast = useRef<Toast>(null);
  const [searchParams] = useSearchParams();

  const [search, setSearch] = useState("");
  const [time, setTime] = useState("");
  const [papelId, setPapelId] = useState(() => papelIdFromSearch(searchParams));
  const [sort, setSort] = useState<UsersSort>(DEFAULT_USERS_SORT);
  const [density, setDensity] = useState<Density>(readStoredDensity);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(ALL_COLUMNS_VISIBLE);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(DEFAULT_PAGE_SIZE);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draftDensity, setDraftDensity] = useState<Density>(readStoredDensity);
  const [draftColumns, setDraftColumns] = useState<string[]>(ALL_COLUMNS_VISIBLE);
  const [editing, setEditing] = useState<User | null>(null);

  /** Usuário em confirmação de ativação/inativação; `null` mantém o modal fechado. */
  const [toggling, setToggling] = useState<User | null>(null);

  const {
    users,
    total,
    semPapel,
    times,
    status,
    errorMessage,
    reload,
    applyUserStatus,
    applyUserRole,
  } = useUsers({
    search,
    time,
    papelId,
    page: Math.floor(first / rows) + 1,
    pageSize: rows,
    sort,
  });
  const roleFilter = useRoleOptions();

  /** AC2 — o arquivo leva o resultado completo da consulta, e não a página em tela. */
  const exportView = useTableExport({
    loadView: async () => {
      const allUsers = await listAllUsers({ pesquisa: search, time, papelId, ordenacao: sort });

      return {
        fileName: EXPORT_FILE_NAME,
        title: EXPORT_TITLE,
        columns: EXPORT_COLUMNS,
        rows: allUsers.map(toExportRow),
      };
    },
    ...createExportFeedback(toast),
  });

  const isLoading = status === "loading";
  const hasSearch = search.trim() !== "";
  const hasFilters = Boolean(time || papelId);
  const hasCriteria = hasSearch || hasFilters;
  const emptyKind = resolveEmptyStateKind(hasSearch, hasFilters);
  const emptyContent =
    emptyKind === "search"
      ? EMPTY_SEARCH_STATE
      : emptyKind === "filter"
        ? EMPTY_FILTER_STATE
        : EMPTY_STATE;

  // Só depois da consulta concluída: enquanto ela corre, a tabela segue em tela
  // com o indicador de carregamento e os registros da consulta anterior.
  const isEmpty = status === "success" && users.length === 0;

  /**
   * O seletor de quantidade por página fica à direita dos botões de navegação e
   * fica bloqueado enquanto a consulta está em curso.
   */
  const paginatorTemplate = useMemo<PaginatorTemplateOptions>(
    () => ({
      layout: "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
      RowsPerPageDropdown: (options) => (
        <Dropdown
          // Mantém a classe do template padrão para o tema alcançar o seletor.
          className="p-paginator-rpp-options"
          panelClassName={styles.rowsPerPagePanel}
          value={options.value}
          options={PAGE_SIZE_DROPDOWN_OPTIONS}
          onChange={options.onChange}
          disabled={isLoading || options.disabled}
          aria-label="Usuários por página"
          data-testid="users-rows-per-page"
        />
      ),
    }),
    [isLoading],
  );

  const { field: sortField, direction: sortDirection } = parseUsersSort(sort);
  const sortOrder = sortDirection === "desc" ? -1 : 1;

  // Um total menor (pesquisa, filtros, recarga) pode deixar a página atual fora da faixa.
  useEffect(() => {
    setFirst((current) => (current < total ? current : 0));
  }, [total]);

  // Pesquisa e filtros compõem um único critério: qualquer mudança volta à página 1.
  function handleSearch(term: string) {
    setSearch(term);
    setFirst(0);
  }

  function handleTime(value: string) {
    setTime(value);
    setFirst(0);
  }

  function handlePapel(value: string) {
    setPapelId(value);
    setFirst(0);
  }

  function handleClearSearch() {
    handleSearch("");
  }

  function handleClearFilters() {
    handleTime("");
    handlePapel("");
  }

  function handleSort(value: UsersSort) {
    setSort(value);
    setFirst(0);
  }

  /**
   * Ordenação pelo cabeçalho conforme os Padrões Globais de Interface: o ciclo
   * é ascendente → descendente → sem ordenação, e "sem ordenação" recompõe o
   * padrão da tela, que é Nome (A–Z).
   */
  function handleColumnSort(event: DataTableStateEvent) {
    const field = event.sortField as UsersSortField | undefined;
    const order = event.sortOrder;

    handleSort(
      field && order ? buildUsersSort(field, order === -1 ? "desc" : "asc") : DEFAULT_USERS_SORT,
    );
  }

  function openSettings() {
    setDraftDensity(density);
    setDraftColumns(visibleColumns);
    setSettingsOpen(true);
    configPanel.current?.hide();
  }

  function closeSettings() {
    setSettingsOpen(false);
  }

  function applySettings() {
    setDensity(draftDensity);
    persistDensity(draftDensity);
    setVisibleColumns(draftColumns);
    setSettingsOpen(false);
  }

  // TODO: reativar com "Mostrar e Ocultar Colunas".
  // function toggleDraftColumn(value: string, checked: boolean) {
  //   setDraftColumns((current) =>
  //     checked
  //       ? ALL_COLUMNS_VISIBLE.filter((column) => column === value || current.includes(column))
  //       : current.filter((column) => column !== value),
  //   );
  // }

  // Trocar a quantidade por página chega aqui com `first` zerado: volta à página 1.
  function handlePage(event: PaginatorPageChangeEvent) {
    setFirst(event.first);
    setRows(event.rows);
  }

  // + Novo Usuário segue como ponto de entrada da US721.
  function handleNewUser() {}

  /** RN04: nada é alterado antes da confirmação no modal. */
  function handleToggleStatus(user: User) {
    setToggling(user);
  }

  /** AC5: a mensagem distingue os motivos da falha e decide se cabe nova tentativa. */
  function showToggleError(error: unknown) {
    const known = isAPIError(error) ? TOGGLE_STATUS_ERRORS[error.status] : undefined;
    if (known) setToggling(null);

    toast.current?.show({
      severity: "error",
      summary: known?.summary ?? TOGGLE_STATUS_ERROR_MESSAGE,
      detail: known?.detail ?? getErrorMessage(error),
      life: 5000,
      closable: true,
    });
  }

  /** Nunca rejeita: a falha de gravação mantém o modal aberto para nova tentativa. */
  async function handleConfirmToggleStatus(user: User) {
    const next: UserStatus = user.status === "ativo" ? "inativo" : "ativo";

    try {
      const applied = await updateUserStatus({ id: user.id, status: next });

      // AC2/AC3: a linha passa a exibir o novo status sem recarregar a página e
      // o perfil associado ao usuário segue como está (RN05). A resposta pode
      // vir sem corpo (ex.: 204), caso em que vale o status enviado.
      applyUserStatus(user.id, applied ?? next);
      setToggling(null);

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: next === "ativo" ? ACTIVATE_SUCCESS_MESSAGE : DEACTIVATE_SUCCESS_MESSAGE,
        life: 3000,
        closable: true,
      });
    } catch (error) {
      showToggleError(error);
    }
  }

  function handleEdit(user: User) {
    setEditing(user);
  }

  function showUpdateError(error: unknown) {
    const known = isAPIError(error) ? UPDATE_ERROR_BY_STATUS[error.status] : undefined;

    toast.current?.show({
      severity: "error",
      summary: known?.summary ?? ROLE_UPDATE_ERROR_MESSAGE,
      detail: known?.detail ?? getErrorMessage(error),
      life: 5000,
      closable: true,
    });
  }

  /** Nunca rejeita: a falha mantém o modal aberto com a seleção feita. */
  async function handleUpdateRole(user: User, { papelId: selected }: EditUserValues) {
    try {
      const updated = await updateUserRole({ id: user.id, papelId: selected });

      // A resposta pode vir vazia (ex.: 204): vale então o perfil escolhido na tela.
      const selectedLabel = roleFilter.options.find((option) => option.value === selected)?.label;

      applyUserRole(user.id, {
        papelId: updated?.papelId || selected,
        papel: updated?.papel || selectedLabel || user.papel,
      });
      setEditing(null);

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: ROLE_UPDATE_SUCCESS_MESSAGE,
        life: 3000,
        closable: true,
      });
    } catch (error) {
      showUpdateError(error);
    }
  }

  /** RN001 — o menu ⋮ abre o modal Exportar Tabela. */
  function handleExportView() {
    configPanel.current?.hide();
    exportView.open();
  }

  function renderHeader(label: string, badge?: ReactNode) {
    return (
      <span className={styles.columnHeader}>
        <span className={styles.columnLabel} data-testid="users-column-label">
          {label}
        </span>
        {badge}
      </span>
    );
  }

  /** Sinalização "Sem perfil" da RN004 no cabeçalho: alerta com a quantidade pendente. */
  function renderPapelBadge() {
    if (semPapel <= 0) return null;

    const description = `${semPapel} ${semPapel === 1 ? "usuário sem perfil" : "usuários sem perfil"}`;

    return (
      <span className={styles.papelBadge} title={description} data-testid="users-no-role-count">
        <i className="pi pi-exclamation-triangle" aria-hidden />
        <span className={styles.srOnly}>{description}</span>
        <span aria-hidden>{semPapel}</span>
      </span>
    );
  }

  function renderPhoto(user: User) {
    return user.fotoUrl ? (
      <Avatar
        className={styles.avatar}
        image={user.fotoUrl}
        imageAlt={`Foto de ${user.nome}`}
        shape="circle"
      />
    ) : (
      <Avatar
        className={styles.avatar}
        label={getUserInitials(user.nome)}
        shape="circle"
        aria-label={`Sem foto de ${user.nome}`}
      />
    );
  }

  function renderEmail(user: User) {
    return (
      <span className={styles.emailText} title={user.email}>
        {user.email || EMPTY_CELL}
      </span>
    );
  }

  function renderPapel(user: User) {
    if (user.papel) return user.papel;

    return (
      <span className={styles.noRole} data-testid={`users-no-role-${user.id}`}>
        <i className="pi pi-exclamation-triangle" aria-hidden />
        {NO_ROLE_LABEL}
      </span>
    );
  }

  function renderStatus(user: User) {
    return (
      <span className={styles.status} data-testid={`users-status-${user.id}`}>
        {USER_STATUS_LABEL[user.status]}
      </span>
    );
  }

  function renderActions(user: User) {
    const active = user.status === "ativo";

    return (
      <div className={styles.actions}>
        <Button
          type="button"
          className={styles.iconButton}
          icon="pi pi-pencil"
          aria-label={`Editar usuário ${user.nome}`}
          tooltip={EDIT_ACTION_TOOLTIP}
          tooltipOptions={ACTION_TOOLTIP_OPTIONS}
          onClick={() => handleEdit(user)}
          data-testid={`users-edit-${user.id}`}
          severity="secondary"
          rounded
          text
        />
        <Button
          type="button"
          className={styles.iconButton}
          icon="pi pi-power-off"
          aria-label={`${active ? "Inativar" : "Ativar"} usuário ${user.nome}`}
          // AC4: o texto acompanha a situação da linha, que é atualizada após a
          // confirmação sem recarregar a listagem.
          tooltip={toggleActionTooltip(active)}
          tooltipOptions={ACTION_TOOLTIP_OPTIONS}
          onClick={() => handleToggleStatus(user)}
          data-testid={`users-toggle-status-${user.id}`}
          severity="secondary"
          rounded
          text
        />
      </div>
    );
  }

  if (status === "forbidden") {
    return (
      <section className={styles.container} data-testid="users-screen">
        <StateMessage
          icon="pi pi-lock"
          title="Acesso não autorizado"
          description={errorMessage ?? undefined}
          testId="users-forbidden"
        />
      </section>
    );
  }

  return (
    <section className={styles.container} data-testid="users-screen">
      <Toast ref={toast} position="top-right" />

      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <div className={styles.searchField}>
            <label className={styles.srOnly} htmlFor="users-search">
              Pesquisar usuários por nome ou e-mail
            </label>
            <InputText
              id="users-search"
              className={styles.searchInput}
              type="search"
              value={search}
              placeholder="Buscar por nome, e-mail..."
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                handleSearch(event.currentTarget.value)
              }
              data-testid="users-search"
            />
            <i className={`pi pi-search ${styles.searchIcon}`} aria-hidden />
          </div>

          {/* Sem seleção o Dropdown exibe o placeholder, que é o nome do filtro. */}
          <Dropdown
            className={styles.filterDropdown}
            panelClassName={styles.filterPanel}
            value={time}
            options={[ANY_TIME, ...times]}
            optionLabel="label"
            optionValue="value"
            placeholder={ANY_TIME.label}
            filter
            filterBy="label"
            filterDelay={0}
            filterPlaceholder={TIME_FILTER_PLACEHOLDER}
            emptyFilterMessage={TIME_EMPTY_FILTER_MESSAGE}
            onChange={(event) => handleTime((event.value as string | null) ?? "")}
            aria-label="Filtrar por time"
            data-testid="users-filter-time"
          />

          <Dropdown
            className={styles.filterDropdown}
            panelClassName={styles.filterPanel}
            value={papelId}
            options={[ANY_ROLE, ...roleFilter.options]}
            optionLabel="label"
            optionValue="value"
            placeholder={ANY_ROLE.label}
            filter
            filterBy="label"
            filterDelay={0}
            filterPlaceholder={ROLE_FILTER_PLACEHOLDER}
            emptyFilterMessage={ROLE_EMPTY_FILTER_MESSAGE}
            onChange={(event) => handlePapel((event.value as string | null) ?? "")}
            emptyMessage={
              roleFilter.hasError
                ? "Não foi possível carregar os perfis."
                : "Nenhum perfil cadastrado."
            }
            disabled={roleFilter.isLoading}
            aria-label="Filtrar por perfil"
            data-testid="users-filter-papel"
          />
        </div>

        <div className={styles.toolbarActions}>
          <Button
            type="button"
            className={styles.newUserButton}
            icon="pi pi-plus"
            label="Novo Usuário"
            onClick={handleNewUser}
            data-testid="users-new"
          />

          <Button
            type="button"
            className={styles.configButton}
            icon="pi pi-ellipsis-v"
            aria-label="Opções da tabela"
            aria-haspopup
            onClick={(event) => configPanel.current?.toggle(event)}
            data-testid="users-table-config"
            severity="secondary"
            outlined
          />

          {/* Menu de detalhes da tabela: os dois pontos de entrada previstos nos padrões globais. */}
          <OverlayPanel ref={configPanel} className={styles.configPanel} dismissable>
            <div className={styles.configBody} data-testid="users-table-config-panel">
              <button
                type="button"
                className={styles.configAction}
                onClick={handleExportView}
                data-testid="users-export-view"
              >
                <i className="pi pi-download" aria-hidden />
                Exportar Visualização
              </button>

              <button
                type="button"
                className={styles.configAction}
                onClick={openSettings}
                data-testid="users-table-settings"
              >
                <i className="pi pi-cog" aria-hidden />
                Configurações da Tabela
              </button>
            </div>
          </OverlayPanel>
        </div>
      </div>

      <Dialog
        visible={settingsOpen}
        onHide={closeSettings}
        header="Configuração da Tabela"
        className={styles.settingsDialog}
        contentClassName={styles.settingsContent}
        modal
        draggable={false}
        resizable={false}
        dismissableMask
        footer={
          <div className={styles.settingsFooter}>
            <Button
              type="button"
              className={styles.settingsCancel}
              label="Cancelar"
              onClick={closeSettings}
              data-testid="users-settings-cancel"
              severity="secondary"
              text
            />
            <Button
              type="button"
              className={styles.settingsApply}
              label="Aplicar"
              onClick={applySettings}
              data-testid="users-settings-apply"
            />
          </div>
        }
      >
        <div className={styles.settingsBody} data-testid="users-settings-dialog">
          <section className={styles.settingsSection}>
            <h3 className={styles.settingsTitle}>Tamanho da tabela</h3>

            <div className={styles.densityGroup} role="radiogroup" aria-label="Tamanho da tabela">
              {DENSITY_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`${styles.densityOption} ${
                    draftDensity === option.value ? styles.densityOptionSelected : ""
                  }`}
                  htmlFor={`users-density-${option.value}`}
                >
                  <input
                    id={`users-density-${option.value}`}
                    className={styles.srOnly}
                    type="radio"
                    name="users-density"
                    value={option.value}
                    checked={draftDensity === option.value}
                    onChange={() => setDraftDensity(option.value)}
                  />
                  <span className={styles.densityRadio} aria-hidden />
                  <span className={styles.densityCopy}>
                    <span className={styles.densityLabel}>{option.label}</span>
                    <span className={styles.densityDescription}>{option.description}</span>
                  </span>
                </label>
              ))}
            </div>
          </section>

          {/* TODO: reativar quando a seleção de colunas estiver alinhada ao Figma.
          <section className={styles.settingsSection}>
            <h3 className={styles.settingsTitle}>Mostrar e Ocultar Colunas</h3>

            <div className={styles.settingsOptions}>
              {COLUMN_OPTIONS.map((option) => (
                <div key={option.value} className={styles.settingsOption}>
                  <Checkbox
                    inputId={`users-column-${option.value}`}
                    name="users-columns"
                    value={option.value}
                    checked={draftColumns.includes(option.value)}
                    onChange={(event) => toggleDraftColumn(option.value, Boolean(event.checked))}
                  />
                  <label className={styles.settingsChoice} htmlFor={`users-column-${option.value}`}>
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          </section>
          */}
        </div>
      </Dialog>

      {status === "error" ? (
        <StateMessage
          icon="pi pi-exclamation-triangle"
          title="Não foi possível carregar os usuários."
          description={errorMessage ?? undefined}
          testId="users-error"
        >
          <Button
            type="button"
            className={styles.retryButton}
            label="Tentar novamente"
            icon="pi pi-refresh"
            onClick={reload}
            data-testid="users-retry"
          />
        </StateMessage>
      ) : (
        <>
          <div className={styles.tableWrapper}>
            {isLoading ? (
              <span className={styles.srOnly} role="status" data-testid="users-loading">
                Carregando usuários...
              </span>
            ) : null}

            {isEmpty ? (
              <EmptyState
                kind={emptyKind}
                content={emptyContent}
                onClear={
                  emptyKind === "search"
                    ? handleClearSearch
                    : emptyKind === "filter"
                      ? handleClearFilters
                      : undefined
                }
                testId="users-empty"
              />
            ) : (
              <DataTable
                className={`${styles.table} ${density === "compact" ? styles.tableCompact : ""}`}
                value={users}
                dataKey="id"
                loading={isLoading}
                tableStyle={{ minWidth: "58rem" }}
                emptyMessage={emptyContent.title}
                lazy
                totalRecords={total}
                first={first}
                rows={rows}
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={handleColumnSort}
                sortIcon={() => null}
                removableSort
                aria-label="Usuários cadastrados"
                data-testid="users-table"
              >
                <Column
                  header={renderHeader("Foto")}
                  headerClassName={styles.photoHeader}
                  bodyClassName={styles.photoCell}
                  style={{ width: "4rem" }}
                  body={renderPhoto}
                />
                <Column
                  field="nome"
                  header={renderHeader("Nome")}
                  style={{ minWidth: "10rem" }}
                  sortable
                />
                <Column
                  field="email"
                  header={renderHeader("E-mail")}
                  style={{ minWidth: "13rem" }}
                  body={renderEmail}
                  sortable
                />
                <Column
                  field="time"
                  header={renderHeader("Time")}
                  style={{ minWidth: "7rem" }}
                  body={(user: User) => user.time || EMPTY_CELL}
                  sortable
                />
                <Column
                  field="papel"
                  header={renderHeader("Perfil", renderPapelBadge())}
                  style={{ minWidth: "8rem" }}
                  body={renderPapel}
                  sortable
                />
                <Column
                  field="ultimaAtividade"
                  header={renderHeader("Última Atividade")}
                  style={{ minWidth: "9.5rem" }}
                  body={(user: User) => formatLastActivity(user.ultimaAtividade)}
                  sortable
                />
                <Column
                  field="status"
                  header={renderHeader("Status")}
                  style={{ minWidth: "6.5rem" }}
                  body={renderStatus}
                  sortable
                />
                <Column
                  header={renderHeader("Ações")}
                  headerClassName={styles.actionsHeader}
                  bodyClassName={styles.actionsCell}
                  style={{ width: "6.375rem" }}
                  body={renderActions}
                />
              </DataTable>
            )}
          </div>

          {total > 0 ? (
            <div className={styles.paginationWrapper}>
              <p className={styles.paginationSummary} data-testid="users-pagination-summary">
                {buildSummary(users.length, total, hasCriteria)}
              </p>

              <Paginator
                className={styles.paginator}
                template={paginatorTemplate}
                first={first}
                rows={rows}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                totalRecords={total}
                onPageChange={handlePage}
                data-testid="users-paginator"
              />
            </div>
          ) : null}
        </>
      )}

      {exportView.isOpen ? (
        <ExportTableDialog
          listName="usuários"
          isExporting={exportView.isExporting}
          onExport={exportView.run}
          onCancel={exportView.close}
        />
      ) : null}

      {toggling ? (
        // A chave descarta o estado de envio ao trocar de usuário.
        <ToggleUserStatusDialog
          key={toggling.id}
          user={toggling}
          onConfirm={() => handleConfirmToggleStatus(toggling)}
          onCancel={() => setToggling(null)}
        />
      ) : null}

      {editing ? (
        // A chave descarta o formulário ao trocar de usuário, sem efeito de sincronização.
        <EditUserDialog
          key={editing.id}
          user={editing}
          roleOptions={roleFilter.options}
          isLoadingRoles={roleFilter.isLoading}
          hasRolesError={roleFilter.hasError}
          onSubmit={(values) => handleUpdateRole(editing, values)}
          onCancel={() => setEditing(null)}
        />
      ) : null}
    </section>
  );
}
